import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../helpers/common'
import { toast } from 'react-toastify'

export const ProductGroupLoadingKeys = {
  getGroupData: "GET_BY_ID",
  getGroupValues: "GET_GROUP_VALUES",
  updateLeadTime: "UPDATE_GROUP_LEAD_TIME",
  updateName: "UPDATE_GROUP_NAME",
  updateSingleGroupData: "UPDATE_SINGLE_GROUP_ID",
  updateSingleProductData: "UPDATE_SINGLE_PRODUCT_IN_GROUP",
  updateProducts: "UPDATE_PRODUCTS",
  updateGroup: "UPDATE_GROUP",
  downloadBarcodes: "DOWNLOAD_ALL_BARCODES",
  deleteVariant: "DELETE_VARIANT",
  deleteGroup: "DELETE_GROUP",
  getVariants: "GET_VARIANT_BY_SEARCH",
  getGroups: "GET_GROUPS_BY_SEARCH",
  addSourceSKUToProducts: "ADD_SOURCE_SKU_TO_THE_PRODUCTS",
  addGroupIdToProducts: "ADD_GROUP_ID_TO_THE_PRODUCTS",
  createGroup: "CREATE_NEW_GROUP",
  createVariant: "CREATE_NEW_VARIANT",
  addVariantToGroup: "ADD_VARIANT_TO_GROUP",
  getVariantsByGroup: "GET_VARIANTS_BY_GROUP",
  getSuppliers: "GET_SUPPLIERS"
}

const initialState = {
    loading: true,
    loadingKeys: [],
    searchedVariants: [],
    searchedGroups: [],
    groupsBySelect: [],
    variantsByGroup: [],
    vendorsToSelect: [],
    error: null,
    groups: [],
    isFirstLoadComplete: false,
    pagination: {
      isTheLastPage: false,
      rangeRows: "",
      totalPages: 0
    },
}

export const createNewVariant = createAsyncThunk(
  'productGroup/createNewVariant',
  async ({ 
    creationData,
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/create-variant`, { creationData });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const createNewGroup = createAsyncThunk(
  'productGroup/createNewGroup',
  async ({ 
    creationData,
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/create-group`, { creationData });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateGroupName = createAsyncThunk(
  'productGroup/updateGroupName',
  async ({ 
    productId, 
    name,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-group-name`, { productId, name });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ id: productId }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateLeadTimeByGroup = createAsyncThunk(
  'productGroup/updateLeadTimeByGroup',
  async ({ 
    productId, 
    leadTime,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-group-lead-time`, { productId, leadTime });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ id: productId }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateProducts = createAsyncThunk(
  'productGroup/updateProducts',
  async ({ 
    productIds, 
    type,
    sourceSku,
    groupId,
    actions,
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-products`, { 
        productIds, 
        actions,
        sourceSku,
        type,
        groupId
      });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateSingleProductInGroup = createAsyncThunk(
  'productGroup/updateSingleProductInGroup',
  async ({
    productId
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/group-product-data-by-id`, { params: {
        productId
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const downloadAllBarcodes = createAsyncThunk(
  'productGroup/downloadAllBarcodes',
  async ({ groupId, statuses }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/generate-barcodes`, {
        groupId,
        ...(Array.isArray(statuses) && statuses.length > 0 ? { statuses } : {}),
      });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const updateSingleGroup = createAsyncThunk(
  'productGroup/updateSingleGroup',
  async ({
    id,
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/group-data-by-id`, { params: {
        id
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const getGroupsData = createAsyncThunk(
    'productGroup/getGroupsData',
    async (params, { rejectWithValue }) => {
      try {
        const { data } = await axios.get(`/api/v1/group-data`, { params: {
          search: params?.search,
          page: params?.page, 
          itemsPerPage: params?.itemsPerPage,
          categories: params?.categories,
          variantStatuses: params?.variantStatuses,
          amazon: params?.amazon
        } });
        return data;
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.message === "canceled") {
          throw new Error("AbortError");
        }
        if ('string' === typeof error.response.data) {
          return rejectWithValue(error.response.data)
        } else {
          return rejectWithValue(error.response.data.errors || error.response.data.error)
        }
      }
    }
)

export const getSuppliers = createAsyncThunk(
  'productGroup/getSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-suppliers-to-select`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
) 

export const getVariantsByGroup = createAsyncThunk(
  'productGroup/getVariantsByGroup',
  async ({
    id
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-variants-by-group`, {
        params: {
          id
        }
      });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const getVariantsBySearch = createAsyncThunk(
  'productGroup/getVariantsBySearch',
  async ({
    search,
    isReplenishable
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-variants-by-search`, {
        params: {
          search,
          isReplenishable
        }
      });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const updateBOMVariantByGroup = createAsyncThunk(
  'products/updateBOMVariantByGroup',
  async ({ 
    productId, 
    bomProductSku,
    type,
    requestToUpdate
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/update-bom-variant-by-group`, { productId, bomProductSku, type });
      if (requestToUpdate) {
        await dispatch(requestToUpdate({ id: productId }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const getGroupsBySearch = createAsyncThunk(
  'productGroup/getGroupsBySearch',
  async ({
    search,
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-groups-by-search`, {
        params: {
          search,
        }
      });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const getGroupsBySelect = createAsyncThunk(
  'productGroup/getGroupsBySelect',
  async ({}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-group-values`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const deleteVariantFromGroup = createAsyncThunk(
  'productGroup/deleteVariantFromGroup',
  async ({
    productId,
    groupId,
    vendorId,
    deleteManually
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`/api/v1/delete-variant-from-group`, { params: {
        productId,
        vendorId,
      }});
      if (deleteManually) {
        dispatch(deleteVariantByGroup({
          groupId,
          productId
        }));
        dispatch(deleteVariantFromVariantByGroup({
          groupId,
          productId
        }));
      }
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const deleteGroup = createAsyncThunk(
  'productGroup/deleteGroup',
  async ({
    groupId,
    deleteManually
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`/api/v1/delete-group`, { params: {
        groupId,
      }});
      if (deleteManually) {
        dispatch(deleteGroupFromList({
          groupId
        }));
      }
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const addVariantToGroup = createAsyncThunk(
  'productGroup/addVariantToGroup',
  async ({
    groupId,
    vendorId,
    variantId
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/add-variant-to-group`, { groupId, variantId, vendorId });
      await dispatch(getVariantsByGroup({
        id: groupId
      }))
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const productGroupSlice = createSlice({
    name: 'productGroup',
    initialState,
    reducers: {
      deleteVariantByGroup: (state, action) => {
        const { groupId, productId } = action.payload;
        const groupIndex = state.groups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          const group = state.groups[groupIndex];
          const variantIndex = (group.variants || []).findIndex(
            (variant) => variant.id === productId
          );
          if (variantIndex !== -1) {
            const updatedVariants = [...group.variants];
            updatedVariants.splice(variantIndex, 1);
            state.groups[groupIndex] = {
              ...group,
              variants: updatedVariants,
            };
          }
        }
      },
      deleteVariantFromVariantByGroup: (state, action) => {
        const { productId } = action.payload;
        const variantIndex = state.variantsByGroup.findIndex(
          (variant) => variant.id === productId
        );
        if (variantIndex !== -1) {
          state.variantsByGroup.splice(variantIndex, 1);
        }
      },
      deleteGroupFromList: (state, action) => {
        const { groupId } = action.payload;
        const groupIndex = state.groups.findIndex((group) => group.id === groupId);
        if (groupIndex !== -1) {
          state.groups.splice(groupIndex, 1);
        }
      },
      clearVariantsByGroup: (state) => {
        state.variantsByGroup = [];
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(createNewVariant.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.createVariant];
        })
        .addCase(createNewVariant.fulfilled, (state, action) => {
            state.loading = false;
            const currentVariant = action?.payload?.variant?.[0];
            const groupId = currentVariant?.group_id;
            const product = currentVariant;
            if (groupId && product) {
              const groupIndex = state.groups.findIndex((group) => group.id === groupId);
              if (groupIndex !== -1) {
                const group = state.groups[groupIndex];
                const updatedVariants = [...group.variants];
                updatedVariants.push(product)
                state.groups[groupIndex] = {
                  ...group,
                  variants: updatedVariants,
                };
              }
            }
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.createVariant, state.loadingKeys)
        })
        .addCase(createNewVariant.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.createVariant, state.loadingKeys)
        })
        .addCase(createNewGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.createGroup];
        })
        .addCase(createNewGroup.fulfilled, (state, action) => {
            state.loading = false;
            state.groups = [...state.groups, action?.payload?.group];
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.createGroup, state.loadingKeys)
            toast.success(
              `The product is added successfully.`, { position: "bottom-right" }
            )
        })
        .addCase(createNewGroup.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.createGroup, state.loadingKeys)
            toast.error(
              `The product can't be added. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(getGroupsData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.getGroupData];
        })
        .addCase(getGroupsData.fulfilled, (state, action) => {
            state.loading = false;
            state.groups = action.payload?.items;
            state.isFirstLoadComplete = true;
            state.pagination.isTheLastPage = action.payload?.isTheLastPage;
            state.pagination.rangeRows = action.payload?.rangeRows;
            state.pagination.totalPages = action.payload?.totalPages;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getGroupData, state.loadingKeys)
        })
        .addCase(getGroupsData.rejected, (state, action) => {
            if (action.error?.message === "AbortError") {
              return;
            }
            state.loading = false;
            state.groups = [];
            state.isFirstLoadComplete = true;
            state.pagination.isTheLastPage = false;
            state.pagination.rangeRows = "";
            state.pagination.totalPages = 0;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getGroupData, state.loadingKeys)
        })
        .addCase(getGroupsBySelect.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.getGroupValues];
        })
        .addCase(getGroupsBySelect.fulfilled, (state, action) => {
          state.loading = false;
          state.groupsBySelect = action.payload;
          state.error = null;
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getGroupValues, state.loadingKeys)
        })
        .addCase(getGroupsBySelect.rejected, (state, action) => {
          state.loading = false;
          state.groupsBySelect = [];
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getGroupValues, state.loadingKeys)
        })
        .addCase(getSuppliers.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.getSuppliers];
        })
        .addCase(getSuppliers.fulfilled, (state, action) => {
            state.loading = false;
            state.vendorsToSelect = action.payload.items;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getSuppliers, state.loadingKeys)
        })
        .addCase(getSuppliers.rejected, (state, action) => {
            state.loading = false;
            state.vendorsToSelect = [];
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getSuppliers, state.loadingKeys)
        })
        .addCase(getVariantsByGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.getVariantsByGroup];
        })
        .addCase(getVariantsByGroup.fulfilled, (state, action) => {
            state.loading = false;
            state.variantsByGroup = action.payload;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getVariantsByGroup, state.loadingKeys)
        })
        .addCase(getVariantsByGroup.rejected, (state, action) => {
            state.loading = false;
            state.variantsByGroup = [];
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getVariantsByGroup, state.loadingKeys)
        })
        .addCase(updateProducts.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.updateProducts];
        })
        .addCase(updateProducts.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateProducts, state.loadingKeys);
            toast.success(
              `Products is updated successfully.`, { position: "bottom-right" }
            )
        })
        .addCase(updateProducts.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateProducts, state.loadingKeys)
            toast.error(
              `Products can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(getGroupsBySearch.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.getGroups];
        })
        .addCase(getGroupsBySearch.fulfilled, (state, action) => {
            state.loading = false;
            state.searchedGroups = action.payload || [];
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getGroups, state.loadingKeys)
        })
        .addCase(getGroupsBySearch.rejected, (state, action) => {
            state.loading = false;
            state.searchedGroups = [];
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getGroups, state.loadingKeys)
        })
        .addCase(getVariantsBySearch.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.getVariants];
        })
        .addCase(getVariantsBySearch.fulfilled, (state, action) => {
            state.loading = false;
            const existingIds = new Set(
              (state.variantsByGroup || []).map((variant) => variant.id)
            );
            state.searchedVariants = (action.payload || []).filter(
              (item) => !existingIds.has(item.id)
            );
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getVariants, state.loadingKeys)
        })
        .addCase(getVariantsBySearch.rejected, (state, action) => {
            state.loading = false;
            state.searchedVariants = [];
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.getVariants, state.loadingKeys)
        })
        .addCase(addVariantToGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.addVariantToGroup];
        })
        .addCase(addVariantToGroup.fulfilled, (state, action) => {
          state.loading = false;
          state.error = null;
          const currentProduct = action?.payload;
          const groupId = currentProduct?.group_id;
          if (currentProduct && groupId) {
            const groupIndex = (state.groups || []).findIndex((group) => group.id === groupId);
            if (groupIndex !== -1) {
              const group = state.groups[groupIndex];
              const variantIndex = (group.variants || []).findIndex(
                (variant) => variant.id === currentProduct.id
              );
              const updatedVariants = [...(group.variants || [])];
              if (variantIndex !== -1) {
                updatedVariants[variantIndex] = {
                  ...updatedVariants[variantIndex],
                  ...currentProduct,
                };
              } else {
                updatedVariants.push(currentProduct);
              }
              state.groups[groupIndex] = {
                ...group,
                variants: updatedVariants,
              };
            }
          }
          const existingIds = new Set(
            (state.variantsByGroup || []).map((variant) => variant.id)
          );
          state.searchedVariants = (state.searchedVariants || []).filter(
            (item) => !existingIds.has(item.id)
          );
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.addVariantToGroup, state.loadingKeys)
          toast.success(
            `The variant is added successfully.`, { position: "bottom-right" }
          )
        })
        .addCase(addVariantToGroup.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.addVariantToGroup, state.loadingKeys)
          toast.error(
            `The variant can't be added. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
        })
        .addCase(updateSingleProductInGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.updateSingleProductData];
        })
        .addCase(updateSingleProductInGroup.fulfilled, (state, action) => {
            const groupId = action?.payload?.group_id;
            const product = action?.payload;
            if (groupId && product) {
              const groupIndex = state.groups.findIndex((group) => group.id === groupId);
              if (groupIndex !== -1) {
                const group = state.groups[groupIndex];
                const variantIndex = group.variants.findIndex(
                  (variant) => variant.id === product.id
                );
                if (variantIndex !== -1) {
                  const updatedVariants = [...group.variants];
                  updatedVariants[variantIndex] = { ...updatedVariants[variantIndex], ...product };
                  state.groups[groupIndex] = {
                    ...group,
                    variants: updatedVariants,
                  };
                }
              }
            }
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateSingleProductData, state.loadingKeys)
        })
        .addCase(updateSingleProductInGroup.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateSingleProductData, state.loadingKeys)
        })
        .addCase(updateSingleGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.updateSingleGroupData];
        })
        .addCase(updateSingleGroup.fulfilled, (state, action) => {
            const groupItem = action?.payload?.item;
            if (groupItem?.id) {
              const index = (state.groups || []).findIndex((group) => group.id === groupItem.id);
              if (index !== -1) {
                const existingGroup = state.groups[index];
                state.groups[index] = {
                  ...existingGroup,
                  id: groupItem.id,
                  name: groupItem.name,
                  lead_time: groupItem.lead_time,
                  bomSkus: groupItem.bomSkus,
                  source: groupItem.source,
                  category_group_id: groupItem.category_group_id,
                  category: groupItem.category,
                  vendor_id: groupItem.vendor_id,
                  variants: (groupItem?.variants || []).map((variant) => {
                    return {
                      ...variant,
                      parentBomSkus: groupItem.bomSkus || null
                    }
                  })
                };
              }
            }
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateSingleGroupData, state.loadingKeys)
        })
        .addCase(updateSingleGroup.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateSingleGroupData, state.loadingKeys)
        })
        .addCase(updateLeadTimeByGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.updateLeadTime];
        })
        .addCase(updateLeadTimeByGroup.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateLeadTime, state.loadingKeys)
            toast.success(
              "The lead time is updated.", 
              { position: "bottom-right" }
            );
        })
        .addCase(updateLeadTimeByGroup.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateLeadTime, state.loadingKeys)
            toast.error(
              `The lead time can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(deleteVariantFromGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.deleteVariant];
        })
        .addCase(deleteVariantFromGroup.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            const existingIds = new Set(
              (state.variantsByGroup || []).map((variant) => variant.id)
            );
            state.searchedVariants = (state.searchedVariants || []).filter(
              (item) => !existingIds.has(item.id)
            );
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.deleteVariant, state.loadingKeys)
            toast.success(
              "The variant is deleted.", 
              { position: "bottom-right" }
            );
        })
        .addCase(deleteVariantFromGroup.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.deleteVariant, state.loadingKeys)
          toast.error(
            `The variant can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
        })
        .addCase(deleteGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.deleteGroup];
        })
        .addCase(deleteGroup.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.deleteGroup, state.loadingKeys)
            toast.success(
              "The product is deleted.", 
              { position: "bottom-right" }
            );
        })
        .addCase(deleteGroup.rejected, (state, action) => {
          state.loading = false;
          state.error = action.error?.message;
          state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.deleteGroup, state.loadingKeys)
          toast.error(
            `The product can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
        })
        .addCase(updateBOMVariantByGroup.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.updateGroup];
        })
        .addCase(updateBOMVariantByGroup.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateGroup, state.loadingKeys)
            toast.success(
              "BOM variant updated successfully.",
              { position: "bottom-right" }
            );
        })
        .addCase(updateBOMVariantByGroup.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateGroup, state.loadingKeys)
            toast.error(
              `Failed to update BOM variant. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(updateGroupName.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.updateName];
        })
        .addCase(updateGroupName.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateName, state.loadingKeys)
            toast.success(
              "The product name is updated.", 
              { position: "bottom-right" }
            );
        })
        .addCase(updateGroupName.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.updateName, state.loadingKeys)
            toast.error(
              `The product name can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(downloadAllBarcodes.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ProductGroupLoadingKeys.downloadBarcodes];
        })
        .addCase(downloadAllBarcodes.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.downloadBarcodes, state.loadingKeys);
            toast.success(
              "Barcodes generated.", 
              { position: "bottom-right" }
            );
        })
        .addCase(downloadAllBarcodes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductGroupLoadingKeys.downloadBarcodes, state.loadingKeys);
            toast.error(
              `The barcodes can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
    }
})

export const { deleteVariantByGroup, clearVariantsByGroup, deleteVariantFromVariantByGroup, deleteGroupFromList } = productGroupSlice.actions;

export default productGroupSlice.reducer